import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, User, Building2, Package } from "lucide-react";
import { toast } from "sonner";
import { useSuiteContext } from "./SuiteContextProvider";

/**
 * CommandPalette - Ctrl+K / Cmd+K.
 *
 * Ricerca full-text tra: assegnazioni operative e cronologia dei contesti.
 * Effettua SOLO cambio contesto (usa `switchContext`). La simulazione
 * (impersonation) e' delegata al componente separato <ImpersonationTool />.
 */
export function CommandPalette() {
  const {
    context, available, history,
    switchContext,
  } = useSuiteContext();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const items = useMemo(() => {
    if (!available) return [];
    const list = [];
    (available?.assignments || []).forEach((a) =>
      list.push({ kind: "assignment", id: `as:${a.id}`,
                  label: a.role_display_name + (a.company_name ? ` · ${a.company_name}` : ""),
                  sub: "Contesto", icon: User, payload: { assignment_id: a.id } }));
    (history || []).slice(0, 6).forEach((h) =>
      list.push({ kind: "history", id: `hist:${h.id}`,
                  label: h.breadcrumb.join(" / "), sub: "Cronologia",
                  icon: User,
                  payload: h.assignment_id ? { assignment_id: h.assignment_id } : null }));
    return list;
  }, [available, history]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((it) =>
      it.label.toLowerCase().includes(term) ||
      it.sub.toLowerCase().includes(term),
    );
  }, [items, q]);

  useEffect(() => { setIdx(0); }, [q, open]);

  const runItem = async (it) => {
    if (!it) return;
    try {
      const ctxNew = await switchContext(it.payload);
      toast.success("Contesto aggiornato", {
        description: (ctxNew?.breadcrumb || []).join(" · "),
      });
      setOpen(false);
      setQ("");
    } catch (e) {
      if (e?.code === "UNSAVED_CHANGES") {
        toast.warning("Modifiche non salvate: apri il selettore contesto");
      } else {
        const detail = e?.response?.data?.detail || e?.message;
        toast.error(`Operazione non riuscita: ${detail}`);
      }
    }
  };

  const onKeyList = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); runItem(filtered[idx]); }
  };

  if (!context) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="suite-command-palette"
        className="p-0 max-w-xl overflow-hidden"
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search size={16} className="text-suite-neutral-400" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyList}
            placeholder="Cerca ruolo, azienda, modulo, utente..."
            className="border-0 shadow-none focus-visible:ring-0"
            data-testid="palette-input"
          />
          <kbd className="hidden rounded bg-suite-neutral-100 px-1.5 py-0.5 text-[10px] text-suite-neutral-500 sm:inline-block">
            Esc
          </kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-xs text-suite-neutral-400">
              Nessun risultato
            </li>
          )}
          {filtered.map((it, i) => {
            const Icon = it.icon;
            return (
              <li
                key={it.id}
                onMouseEnter={() => setIdx(i)}
                onClick={() => runItem(it)}
                className={
                  "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm " +
                  (i === idx ? "bg-suite-primary/10" : "hover:bg-suite-neutral-50")
                }
                data-testid={`palette-item-${it.id}`}
              >
                <Icon size={14} className="text-suite-neutral-500" />
                <span className="flex-1 truncate">{it.label}</span>
                <span className="text-xs text-suite-neutral-400">{it.sub}</span>
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
